import { Form, Alert } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { ControlledFormItem, Button } from '@/shared/components/ui/atoms';
import type { LoginFormData, LoginFormProps } from './types';
import './LoginForm.css';
import { Link } from 'react-router-dom';
import { useAllowMeCollect } from '@/shared/hooks/useAllowMeCollect';
import { useEffect, useRef } from 'react';
import { useAllowMeStore } from '@/shared/store/allowme';
import ReportIcon from '@/assets/icons/report.svg?react';
import CloseSmallIcon from '@/assets/icons/close_small.svg?react';
import { isSsoFederationAllowed } from '@/shared/config/featureFlags';

/**
 * @description LoginForm Organism — login form with email/password authentication.
 * Uses React Hook Form + Zod for validation. Error alert uses custom SVG icons
 * (report octagon + close_small) matching the Figma design system (DUFP-44).
 *
 * Federated (SSO) states, per Figma "[PU] Home Unificada" (page 1601:18441):
 * - `ssoState="identified"` — Figma "[Erro 01] Federado tenta login local"
 *   (CA05): the design-system alert "SSO Identificado: ..." renders, the
 *   password field is removed (label "Email corporativo") and the primary
 *   action becomes "Acessar via SSO" (conducting the user to `/sso` with the
 *   typed e-mail), with "Voltar para o login" below it.
 * - `ssoState="not-linked"` — Figma "[Login] Não federado tenta acessa via
 *   SSO" (CA10): the error alert "Erro: Conta não vinculada a SSO. ..."
 *   renders with the helper line "Utilize seu e-mail e senha para acessar a
 *   plataforma." above the regular e-mail+password form (e-mail preserved).
 *
 * @param {LoginFormProps} props - LoginForm component props
 * @returns {React.ReactElement} A complete login form with validation
 * @example
 * <LoginForm onSubmit={handleLogin} loading={false} error={null} />
 */
const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  loading = false,
  error = null,
  ssoState = null,
  onAccessSso,
  onBackToLogin,
  defaultEmail = '',
}) => {
  // Trigger AllowMe device collection on render
  useAllowMeCollect();
  // Reset deviceCollectId on unmount to ensure a new one is collected each time
  useEffect(() => {
    return () => {
      useAllowMeStore.getState().reset();
    };
  }, []);
  const { t } = useLanguage();

  const ssoIdentified = ssoState === 'identified';

  const loginSchema = z.object({
    username: z.string().min(1, t('Email is required')),
    password: z.string().min(1, t('Password is required')),
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: defaultEmail,
      password: '',
    },
  });

  const usernameValue = watch('username');
  const passwordValue = watch('password');
  const isFormEmpty = !usernameValue.trim() || !passwordValue.trim();

  const isSubmittingRef = useRef(false);

  const handleFormSubmit = async (data: LoginFormData) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      await onSubmit(data);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <div className="login-form-wrapper">
      <div className="login-form-header">
        <div className="login-form-header-content">
          <p className="login-form-header-small">{t('Platform Name')}</p>
          <h1 className="login-form-header-title">
            {t('Access your account')}
          </h1>
        </div>

        {!ssoIdentified && (
          <div className="login-form-header-description">
            <p>{t('To login fill your email and password')}</p>
          </div>
        )}
      </div>

      {ssoIdentified && (
        <Alert
          message={
            <span role="status" aria-live="polite">
              <span className="login-alert-label">{t('SSO identified:')}</span>{' '}
              <span className="login-alert-message">
                {t(
                  'Your email is linked to a corporate login. Use SSO access to sign in to the platform.'
                )}
              </span>
            </span>
          }
          type="error"
          showIcon
          closable
          icon={<ReportIcon className="login-alert-icon" />}
          closeIcon={
            <CloseSmallIcon
              className="login-alert-close-icon"
              aria-label="Close"
            />
          }
        />
      )}

      {ssoState === 'not-linked' && !error && (
        <>
          <Alert
            message={
              <span role="status" aria-live="polite">
                <span className="login-alert-label">
                  {t('Error: Account not linked to SSO.')}
                </span>{' '}
                <span className="login-alert-message">
                  {t(
                    'Your email is not associated with any company with corporate authentication (SSO).'
                  )}
                </span>
              </span>
            }
            type="error"
            showIcon
            icon={<ReportIcon className="login-alert-icon" />}
          />
          <p className="login-form-sso-helper">
            {t('Use your email and password to access the platform.')}
          </p>
        </>
      )}

      {/* Autenticação concluída, mas nenhuma business account acessível tem
          plataforma vinculada. Não apresentar como erro de credenciais. */}
      {ssoState === 'access-not-granted' && (
        <Alert
          message={
            <span>
              <span className="login-alert-label">{t('Access denied:')}</span>{' '}
              {t('You do not have permission to access this platform')}{' '}
            </span>
          }
          type="error"
          showIcon
          icon={<ReportIcon className="login-alert-icon" />}
        />
      )}

      {error && !ssoIdentified && (
        <Alert
          message={
            <span>
              <span className="login-alert-label">{t('Attention!')}</span>{' '}
              <span className="login-alert-message">{error}</span>
            </span>
          }
          type="error"
          showIcon
          closable
          icon={<ReportIcon className="login-alert-icon" />}
          closeIcon={
            <CloseSmallIcon
              className="login-alert-close-icon"
              aria-label="Close"
            />
          }
        />
      )}

      {ssoIdentified ? (
        // Figma "[Erro 01]": e-mail preservado, sem campo de senha; a ação
        // primária conduz ao fluxo corporativo sem redigitar o e-mail (CA05).
        <Form layout="vertical" noValidate className="login-form">
          <ControlledFormItem
            name="username"
            control={control as never}
            errors={{}}
            label={t('Corporate email')}
            inputType="text"
            inputProps={{
              placeholder: t('Enter your e-mail here'),
              autoComplete: 'email',
            }}
          />

          <Form.Item>
            <Button
              htmlType="button"
              variant="primary"
              size="lg"
              maxWidth={326}
              disabled={!usernameValue.trim() || loading}
              onClick={() => onAccessSso?.(usernameValue.trim())}
            >
              {t('Access via SSO')}
            </Button>
          </Form.Item>

          <Button
            variant="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => onBackToLogin?.()}
          >
            {t('Back to Login')}
          </Button>
        </Form>
      ) : (
        <Form
          layout="vertical"
          onFinish={handleSubmit(handleFormSubmit)}
          noValidate
          className="login-form"
        >
          <ControlledFormItem
            name="username"
            control={control as never}
            errors={error ? { username: { message: '' } } : {}}
            label={t('Email')}
            inputType="text"
            inputProps={{
              placeholder: t('Enter your e-mail here'),
              autoComplete: 'email',
            }}
          />

          <ControlledFormItem
            name="password"
            control={control as never}
            errors={error ? { password: { message: '' } } : {}}
            label={t('Password')}
            inputType="password"
            inputProps={{
              placeholder: t('Enter your password here'),
              autoComplete: 'current-password',
            }}
            formItemProps={{ className: 'login-form-password-item' }}
          />

          <Link to="/forgot-password" className="forgot-password-link">
            {t('Forgot your password?')}
          </Link>

          <Form.Item>
            <Button
              htmlType="submit"
              variant="primary"
              size="lg"
              maxWidth={326}
              disabled={isFormEmpty || loading || isSubmitting}
            >
              {t('Login')}
            </Button>
          </Form.Item>
        </Form>
      )}

      {isSsoFederationAllowed() && !ssoIdentified && (
        <div className="login-form-sso">
          <hr className="login-form-sso-divider" aria-hidden="true" />
          <Link to="/sso" className="login-form-sso-link">
            {t('Access via SSO')}
          </Link>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
